/**
 * Heart-toggle bookmark button. Sits absolutely-positioned over a property
 * image; stops event propagation so tapping it doesn't navigate to the
 * property page. Uses `useFavourites` so state stays in sync across every
 * card on the page + the /my-favourites list.
 */
import { Heart } from "lucide-react";
import { toast } from "sonner";
import { useFavourites } from "@/hooks/useFavourites";

export default function FavouriteButton({ property, className = "", size = "md" }) {
  const { isFavourite, toggle } = useFavourites();
  if (!property?.property_id) return null;
  const active = isFavourite(property.property_id);

  const onClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const { favorited, error } = await toggle(property);
    if (error) {
      toast.error("Couldn't save favourite");
      return;
    }
    toast.success(favorited ? "Saved to favourites" : "Removed from favourites");
  };

  const sizeCls = size === "sm" ? "h-7 w-7" : "h-9 w-9";
  const iconCls = size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4";

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={active ? "Remove from favourites" : "Save to favourites"}
      aria-pressed={active}
      title={active ? "Remove from favourites" : "Save to favourites"}
      className={`inline-flex items-center justify-center rounded-full bg-white/90 backdrop-blur-sm shadow-md hover:scale-110 hover:bg-white transition-transform ${sizeCls} ${className}`}
      data-testid={`favourite-toggle-${property.property_id}`}
    >
      <Heart
        className={`${iconCls} transition-colors ${active ? "fill-red-500 text-red-500" : "text-gray-700"}`}
      />
    </button>
  );
}
